import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertTriangle, ShieldCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';

/**
 * Single-use claim link from a "connect your payment" email. The raw token only
 * travels to the payment-reconcile function (which stores its hash). Signing in is
 * required; if the account email differs from the payment email an admin must approve.
 */
const KEY = 'gg_claim_token';

const MESSAGES: Record<string, { en: string; es: string; ok?: boolean }> = {
  fulfilled: { en: 'Your payment is now applied to this account. Your credits are ready.', es: 'Su pago ya está aplicado a esta cuenta. Sus créditos están listos.', ok: true },
  pending_admin_review: { en: 'Thanks. This account uses a different email than the payment, so our team will verify and apply it — usually within one business day.', es: 'Gracias. Esta cuenta usa un correo distinto al del pago; nuestro equipo lo verificará y aplicará, normalmente en un día hábil.', ok: true },
  already_used: { en: 'This link has already been used.', es: 'Este enlace ya fue usado.' },
  expired: { en: 'This link has expired. Contact support@giggone.com and we will send a new one.', es: 'Este enlace venció. Escriba a support@giggone.com y le enviaremos uno nuevo.' },
  superseded: { en: 'A newer link was sent to you. Please use the most recent email.', es: 'Se le envió un enlace más reciente. Use el último correo.' },
  already_fulfilled: { en: 'This payment has already been applied.', es: 'Este pago ya fue aplicado.', ok: true },
  claim_already_pending: { en: 'A claim for this payment is already under review.', es: 'Ya hay una solicitud en revisión para este pago.' },
  rate_limited: { en: 'Too many attempts. Please try again in an hour.', es: 'Demasiados intentos. Intente de nuevo en una hora.' },
  invalid: { en: 'This link is not valid.', es: 'Este enlace no es válido.' },
};

const ClaimPayment: React.FC = () => {
  const { profile, loading } = useAuth();
  const { path, lang } = useI18n();
  const es = lang === 'es';
  const [token] = useState(() => {
    const q = new URLSearchParams(window.location.search).get('token') || '';
    if (q) {
      sessionStorage.setItem(KEY, q);
      window.history.replaceState(null, '', window.location.pathname);
      return q;
    }
    return sessionStorage.getItem(KEY) || '';
  });
  const [state, setState] = useState<'idle' | 'working' | 'done'>('idle');
  const [status, setStatus] = useState('');

  const claim = async () => {
    setState('working');
    const { data, error } = await supabase.functions.invoke('payment-reconcile', { body: { action: 'claim', token } });
    const s = String(data?.status || (error ? 'error' : 'invalid'));
    setStatus(s);
    setState('done');
    if (['fulfilled', 'pending_admin_review', 'already_used', 'already_fulfilled'].includes(s)) sessionStorage.removeItem(KEY);
  };

  useEffect(() => {
    if (!loading && profile && token && state === 'idle') void claim();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, profile, token]);

  const m = MESSAGES[status];
  return (
    <div className="mx-auto max-w-xl px-4 py-16">
      <Seo title={es ? 'Aplicar mi pago' : 'Apply my payment'} description="" noindex />
      <div className="rounded-2xl border border-slate-200 bg-white p-8">
        <h1 className="flex items-center gap-2 text-2xl font-extrabold text-slate-900">
          <ShieldCheck className="h-6 w-6 text-orange-500" /> {es ? 'Aplicar mi pago' : 'Apply my payment'}
        </h1>
        {!token ? (
          <p className="mt-4 text-sm text-slate-600">{MESSAGES.invalid[es ? 'es' : 'en']}</p>
        ) : loading ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> …</p>
        ) : !profile ? (
          <div className="mt-4 space-y-4 text-sm text-slate-600">
            <p>{es
              ? 'Inicie sesión, o cree su cuenta con el MISMO correo que usó para pagar. Luego vuelva a abrir el enlace de su correo; sigue siendo válido hasta que se use.'
              : 'Sign in, or create your account with the SAME email you paid with. Then open the link in your email again — it stays valid until it is used.'}</p>
            <div className="flex gap-3">
              <Link to={path('/login')} className="rounded-lg bg-slate-900 px-4 py-2 font-bold text-white">{es ? 'Iniciar sesión' : 'Sign in'}</Link>
              <Link to={path('/register')} className="rounded-lg border border-slate-300 px-4 py-2 font-bold text-slate-800">{es ? 'Crear cuenta' : 'Create account'}</Link>
            </div>
          </div>
        ) : state !== 'done' ? (
          <p className="mt-4 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> {es ? 'Verificando…' : 'Verifying…'}</p>
        ) : (
          <div className={`mt-4 flex items-start gap-2 rounded-xl p-4 text-sm ${m?.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'}`}>
            {m?.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4" /> : <AlertTriangle className="mt-0.5 h-4 w-4" />}
            <div>
              <p>{m ? m[es ? 'es' : 'en'] : (es ? 'No se pudo completar. Escriba a support@giggone.com.' : 'This could not be completed. Contact support@giggone.com.')}</p>
              {status === 'fulfilled' ? <Link to={path('/dashboard')} className="mt-2 inline-block font-bold underline">{es ? 'Ir a mi panel' : 'Go to my dashboard'}</Link> : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimPayment;
